/**
 * OpsStaffingRoster — Operations Dashboard staffing view. Replaces the
 * old "Schedules" tab per the Feb 2026 CEO directive: Ops managers
 * need a single roster that maps every field agent to their role,
 * position, region (with color), state, actual shift schedule, and
 * status. All CRUD lives in HR → Employees (Command Center · Access
 * Areas). This view is read-only.
 *
 * Columns (per directive):
 *   • Name (badge_number underneath)
 *   • Role — Valet | Cleaner | Valet/Cleaner (supervisors + regional
 *     managers do both, so they render as "Valet/Cleaner")
 *   • Position — Manager | Supervisor | Lead | Agent
 *   • Region — full interpretation like "R07 Central" with the
 *     region's representative color dot
 *   • State
 *   • Schedule — their actual shift schedule (day + time window)
 *   • Status — Active / Blocked
 */
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Users, Search } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useAdminScopeTerms } from '../lib/adminScopeTerms';

const API = process.env.REACT_APP_BACKEND_URL;

// -- Field-work role interpretation per CEO directive -------------
// Valet + Cleaner are the two functional buckets Ops dispatches to.
// Supervisors and Regional Managers can work BOTH kinds of jobs so we
// surface them as "Valet/Cleaner".
function interpretRole(employee, view) {
  if (view === 'valet') return 'Valet';
  if (view === 'cleaners') return 'Cleaner';
  const service = String(employee.primary_service || employee.service_line || employee.role_type || '').toLowerCase();
  if (['cleaning', 'cleaner', 'cleaners', 'commercial'].includes(service)) return 'Cleaner';
  if (['valet', 'valet_trash', 'multifamily'].includes(service)) return 'Valet';
  return 'Field staff';
}

// -- Position rank per CEO directive ------------------------------
//   regional_manager → Manager
//   supervisor       → Supervisor
//   crew_leader      → Lead
//   everyone else    → Agent (this is the field default)
function interpretPosition(rawRole, rawPosition) {
  if (rawPosition && rawPosition.trim()) return rawPosition;
  const r = (rawRole || '').toLowerCase();
  if (r === 'regional_manager' || r === 'manager') return 'Manager';
  if (r === 'supervisor') return 'Supervisor';
  if (r === 'crew_leader' || r === 'lead') return 'Lead';
  return 'Agent';
}

function inferState(regionCode) {
  if (!regionCode) return 'TX';
  const m = /R\d{2}-([A-Z]{2})/.exec(String(regionCode).toUpperCase());
  return m ? m[1] : 'TX';
}

// -- Shift → human-readable schedule text -------------------------
// Backend stores shift as short codes (AM/PM/OVN/ALL) and optionally a
// shift_window / shift_days. We collapse whatever's on file into a
// single string so Ops sees "MON-FRI · 8AM-12PM" style output.
function interpretSchedule(emp) {
  const shift = (emp.shift || '').toUpperCase();
  const map = {
    AM: 'AM · 6-11AM',
    PM: 'PM · 12-6PM',
    OVN: 'Overnight · 6PM-2AM',
    ALL: 'Flex · varies',
  };
  const base = map[shift] || emp.shift_window || emp.shift;
  if (!base) {
    // No shift data on file — surface a friendly "Not scheduled" so
    // Ops managers know it's an HR data gap, not a rendering bug.
    return 'Not scheduled';
  }
  const days = emp.shift_days || emp.working_days;
  if (Array.isArray(days) && days.length > 0) {
    return `${days.slice(0, 5).join(', ').toUpperCase()} · ${base}`;
  }
  return base;
}

export default function OpsStaffingRoster({ employees = [] }) {
  const terms = useAdminScopeTerms();
  const [q, setQ] = useState('');
  // Big City tab strip (Dallas · Houston · Austin · San Antonio · Tyler)
  // — matches the metro switcher used across Routes & Regions and
  // every Operations drill-down. Default Dallas.
  const [bigCityFilter, setBigCityFilter] = useState('');
  const [stateFilter, setStateFilter] = useState('all');
  const [regionFilter, setRegionFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [regions, setRegions] = useState([]);
  // Section 5 (iter-136) — Ops Field Staff Dispatch List needs two
  // dedicated views: Site Agent — Valet vs Site Agent — Cleaners.
  // Tab strip below drives a role_type / primary_service filter.
  const [serviceLineFilter, setServiceLineFilter] = useState('all');
  // Section 1 (iter-137) — discrete Position + Role dropdowns alongside
  // the existing State + Region filters.
  const [positionFilter, setPositionFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  // Section 1 (iter-137) — real dispatch status sourced from
  // staff_assignments. We index by user_id so the roster table can
  // render Assigned · Idle · On-Job instead of the is_blocked-based
  // Active/Blocked chip.
  const [assignmentByUser, setAssignmentByUser] = useState({});

  // Regions provide color + full display name for the Region column.
  // We fetch once per mount — the catalog is small (~15 rows) and
  // effectively static after the DFW seed.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${API}/api/routes/regions`, { credentials: 'include' });
        if (res.ok && alive) {
          const data = await res.json();
          setRegions(Array.isArray(data) ? data : (data.regions || []));
        }
      } catch (_) { /* noop */ }
    })();
    return () => { alive = false; };
  }, []);

  // Section 1 (iter-137) — pull the current standing assignments so we
  // can classify each field-staff row as On-Job / Assigned / Idle.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${API}/api/company/staff-assignments`, { credentials: 'include' });
        if (res.ok && alive) {
          const data = await res.json();
          const arr = Array.isArray(data) ? data : (data.assignments || []);
          const idx = {};
          arr.forEach((a) => {
            if (a?.user_id) idx[a.user_id] = a;
          });
          setAssignmentByUser(idx);
        }
      } catch (_) { /* noop */ }
    })();
    return () => { alive = false; };
  }, []);

  const dispatchStatusOf = (e) => {
    // On-Job wins — surfaced from the user record if a live job is
    // running (checked in daily job pushes / clock-in state).
    if (e.on_job || e.active_job_id || e.current_job_id) return 'on-job';
    const a = assignmentByUser[e.user_id];
    if (a && ((a.assigned_properties || []).length > 0 || (a.assigned_cities || []).length > 0)) return 'assigned';
    return 'idle';
  };

  const regionByCode = useMemo(() => {
    const map = {};
    regions.forEach((r) => {
      if (r.region_code) map[r.region_code] = r;
    });
    return map;
  }, [regions]);

  // -- State inference from a region code -----------------------
  // The DFW catalog uses bare "R##" codes (no state suffix) —
  // those all belong to Texas since the DFW Metroplex is settled.
  // If a future region ever ships with a "R##-XX" code, the XX
  // wins.
  const stateFor = (code) => {
    if (!code) return null;
    const suffix = /R\d{2}-([A-Z]{2})/.exec(String(code).toUpperCase());
    if (suffix) return suffix[1];
    if (/^R\d{2}$/i.test(code)) return 'TX'; // DFW is Texas
    return null;
  };

  // Universe of states offered in the State selector. NECS is
  // Dallas-HQ'd today but keep a full US-state catalog so the roster
  // scales cleanly as we expand outside Texas. States that have
  // employees or regions in the DB float to the TOP of the list so
  // the most-used states are still one click away.
  const US_STATES = [
    ['AL', 'Alabama'], ['AK', 'Alaska'], ['AZ', 'Arizona'], ['AR', 'Arkansas'],
    ['CA', 'California'], ['CO', 'Colorado'], ['CT', 'Connecticut'], ['DE', 'Delaware'],
    ['FL', 'Florida'], ['GA', 'Georgia'], ['HI', 'Hawaii'], ['ID', 'Idaho'],
    ['IL', 'Illinois'], ['IN', 'Indiana'], ['IA', 'Iowa'], ['KS', 'Kansas'],
    ['KY', 'Kentucky'], ['LA', 'Louisiana'], ['ME', 'Maine'], ['MD', 'Maryland'],
    ['MA', 'Massachusetts'], ['MI', 'Michigan'], ['MN', 'Minnesota'], ['MS', 'Mississippi'],
    ['MO', 'Missouri'], ['MT', 'Montana'], ['NE', 'Nebraska'], ['NV', 'Nevada'],
    ['NH', 'New Hampshire'], ['NJ', 'New Jersey'], ['NM', 'New Mexico'], ['NY', 'New York'],
    ['NC', 'North Carolina'], ['ND', 'North Dakota'], ['OH', 'Ohio'], ['OK', 'Oklahoma'],
    ['OR', 'Oregon'], ['PA', 'Pennsylvania'], ['RI', 'Rhode Island'], ['SC', 'South Carolina'],
    ['SD', 'South Dakota'], ['TN', 'Tennessee'], ['TX', 'Texas'], ['UT', 'Utah'],
    ['VT', 'Vermont'], ['VA', 'Virginia'], ['WA', 'Washington'], ['WV', 'West Virginia'],
    ['WI', 'Wisconsin'], ['WY', 'Wyoming'],
  ];
  const stateNameByCode = Object.fromEntries(US_STATES);

  const availableStates = useMemo(() => {
    // Pinned states — those that already own a region or an employee.
    const pinned = new Set();
    regions.forEach((r) => {
      const s = stateFor(r.region_code);
      if (s) pinned.add(s);
    });
    employees.forEach((e) => {
      if (e.state) pinned.add(String(e.state).toUpperCase().slice(0, 2));
    });
    // Compose the final list: pinned first (sorted), then the rest of
    // the US catalog, minus duplicates.
    const pinnedSorted = Array.from(pinned).sort();
    const rest = US_STATES.map(([code]) => code).filter((c) => !pinned.has(c));
    return [...pinnedSorted, ...rest];
  }, [regions, employees]);

  // Region dropdown options — regions are ONLY offered once a state
  // is chosen. Before a state pick the dropdown is intentionally
  // empty so the admin makes an explicit state-first decision.
  const regionOptions = useMemo(() => {
    if (stateFilter === 'all') {
      // No state chosen yet → no regions available.
      return [];
    }
    // State-scoped: every region in the catalog that belongs to that
    // state (Texas → all 15 DFW regions R01…R15).
    return regions
      .filter((r) => stateFor(r.region_code) === stateFilter)
      .map((r) => r.region_code)
      .sort();
  }, [stateFilter, regions]);

  // Reset the region filter when the state changes so we never carry
  // over a stale code that no longer belongs to the new state.
  useEffect(() => {
    if (regionFilter !== 'all' && !regionOptions.includes(regionFilter)) {
      setRegionFilter('all');
    }
  }, [regionOptions, regionFilter]);

  // Only staffing agents (field-ops roles). Office roles like admin /
  // sales / hr are hidden since this is a dispatch view. Also
  // filtered by the active Big City tab so managers see only the
  // agents in their metro.
  const roster = useMemo(() => {
    const FIELD_ROLES = new Set([
      'supervisor', 'regional_manager', 'manager',
      'crew_leader', 'lead', 'agent',
      'driver', 'valet', 'staff', 'site_agent', 'cleaner',
    ]);
    // Map region_code → big_city so we can filter employees by metro.
    const bigCityByCode = {};
    regions.forEach((r) => {
      if (r.region_code) bigCityByCode[r.region_code] = r.big_city || 'Unassigned';
    });
    return (employees || [])
      .filter((e) => FIELD_ROLES.has((e.role || '').toLowerCase()))
      .filter((e) => {
        // Section 1 (iter-137) — status filter now maps to real dispatch
        // state (Assigned / Idle / On-Job) plus a "blocked" escape hatch.
        if (statusFilter === 'all') return true;
        if (statusFilter === 'blocked') return !!e.is_blocked;
        return dispatchStatusOf(e) === statusFilter;
      })
      .filter((e) => (roleFilter === 'all' ? true : (e.role || '').toLowerCase() === roleFilter))
      .filter((e) => {
        if (positionFilter === 'all') return true;
        return interpretPosition(e.role, e.position) === positionFilter;
      })
      .filter((e) => {
        if (!bigCityFilter) return true;
        // Employees inherit big city from their region_code. If they
        // have no region on file we fall back to showing them so ops
        // can still assign them; the count still reflects reality.
        if (!e.region_code) return true;
        return bigCityByCode[e.region_code] === bigCityFilter;
      })
      .filter((e) => {
        if (stateFilter === 'all') return true;
        const s = e.state ? String(e.state).toUpperCase().slice(0, 2) : stateFor(e.region_code);
        return s === stateFilter;
      })
      .filter((e) => (regionFilter === 'all' ? true : e.region_code === regionFilter))
      .filter((e) => {
        // Section 5 (iter-136) — service-line dispatch filter.
        if (serviceLineFilter === 'all') return true;
        const rt = (e.role_type || '').toLowerCase();
        const ps = (e.primary_service || '').toLowerCase();
        if (serviceLineFilter === 'valet') return rt === 'valet' || ps === 'valet';
        if (serviceLineFilter === 'cleaners') return rt === 'cleaner' || rt === 'cleaners' || ps === 'cleaning' || ps === 'cleaners';
        return true;
      })
      .filter((e) => {
        if (!q) return true;
        const hay = `${e.name || ''} ${e.badge_number || ''} ${e.position || ''}`.toLowerCase();
        return hay.includes(q.toLowerCase());
      });
  }, [employees, q, regionFilter, statusFilter, stateFilter, bigCityFilter, serviceLineFilter, positionFilter, roleFilter, assignmentByUser, regions]);

  const availableRegions = regionOptions;

  return (
    <div className="space-y-4" data-testid="ops-staffing-roster">
      <Card className="bg-zinc-900 border-zinc-700 p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-2xl font-black text-white flex items-center gap-2"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              <Users className="h-6 w-6 text-[#C9A962]" />
              {terms.staff.toUpperCase()} · READ-ONLY
            </h2>
            <p className="text-xs text-zinc-400 mt-1">
              Operations dispatch view · Add / edit / activate employees is handled by
              <span className="text-[#C9A962] font-semibold"> Human Resources → Employees</span>.
            </p>
          </div>
          <Badge className="bg-[#C9A962] text-black font-bold">{roster.length} agents shown</Badge>
        </div>

        {/* ── Big City tab strip — consistent metro switcher used app-wide.
            Selecting a tab scopes the roster and the Region dropdown
            below. */}
        <div
          className="flex items-center justify-between gap-3 flex-wrap mt-4 pt-4 border-t border-zinc-800"
          data-testid="ops-staffing-bigcity-tabs"
        >
          <div className="flex items-center gap-1.5 flex-wrap">
            {['', 'Dallas', 'Houston', 'Austin', 'San Antonio', 'Tyler'].map((c) => {
              const active = bigCityFilter === c;
              return (
                <button
                  key={c}
                  type="button"
                  onClick={() => { setBigCityFilter(c); setRegionFilter('all'); }}
                  className={`px-3.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-widest transition-all border ${
                    active
                      ? 'bg-[#FFD60A] text-black border-[#FFD60A] shadow-[0_0_0_1px_#FFD60A]'
                      : 'bg-zinc-950 text-zinc-300 border-zinc-700 hover:border-[#C9A962] hover:text-[#C9A962]'
                  }`}
                  data-testid={`ops-staffing-bigcity-tab-${c.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {c || 'All cities'}
                </button>
              );
            })}
          </div>
          <Badge className="bg-zinc-800 text-[#C9A962] font-mono text-[10px]">
            Scope: {bigCityFilter || 'All selected cities'}
          </Badge>
        </div>

        {/* Section 5 (iter-136) — Field Staff Dispatch service-line
            selector. Two dedicated views for Ops: Site Agent — Valet
            vs Site Agent — Cleaners, plus an "All" for combined. */}
        <div
          className="flex items-center gap-1.5 mt-3 pt-3 border-t border-zinc-800 flex-wrap"
          data-testid="ops-dispatch-service-tabs"
        >
          <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mr-1">Dispatch line</span>
          {[
            { k: 'all', label: 'All Field Staff' },
            { k: 'valet', label: 'Site Agent — Valet' },
            { k: 'cleaners', label: 'Site Agent — Cleaners' },
          ].filter(({ k }) => terms.view === 'corporate' || k === 'all').map(({ k, label }) => {
            const active = serviceLineFilter === k;
            return (
              <button
                key={k}
                type="button"
                onClick={() => setServiceLineFilter(k)}
                className={`px-3 py-1 rounded text-[11px] font-bold uppercase tracking-widest transition-all border ${
                  active
                    ? 'bg-cyan-500 text-black border-cyan-500'
                    : 'bg-zinc-950 text-zinc-300 border-zinc-700 hover:border-cyan-500 hover:text-cyan-400'
                }`}
                data-testid={`ops-dispatch-line-${k}`}
              >
                {terms.text(label)}
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-6 gap-3 mt-4">
          <div className="relative md:col-span-2">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-zinc-500" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, badge, position…"
              className="pl-8 bg-zinc-950 border-zinc-700 text-white"
              data-testid="ops-staffing-search"
            />
          </div>
          <Select value={stateFilter} onValueChange={setStateFilter}>
            <SelectTrigger className="bg-zinc-950 border-zinc-700 text-white" data-testid="ops-staffing-state">
              <SelectValue placeholder="States" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">States</SelectItem>
              {availableStates.map((s) => (
                <SelectItem key={s} value={s}>
                  {s}{stateNameByCode[s] ? ` — ${stateNameByCode[s]}` : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={regionFilter} onValueChange={setRegionFilter}
                  disabled={stateFilter === 'all'}>
            <SelectTrigger
              className={`bg-zinc-950 border-zinc-700 text-white ${
                stateFilter === 'all' ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              data-testid="ops-staffing-region"
            >
              <SelectValue placeholder={stateFilter === 'all' ? 'Pick a state first' : 'Regions'} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Regions</SelectItem>
              {availableRegions.map((r) => {
                const reg = regionByCode[r];
                return (
                  <SelectItem key={r} value={r}>
                    {r} {reg?.name ? `— ${reg.name}` : ''}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
          {/* Section 1 (iter-137) — discrete Position filter. */}
          <Select value={positionFilter} onValueChange={setPositionFilter}>
            <SelectTrigger className="bg-zinc-950 border-zinc-700 text-white" data-testid="ops-staffing-position">
              <SelectValue placeholder="Position" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All positions</SelectItem>
              <SelectItem value="Manager">Manager</SelectItem>
              <SelectItem value="Supervisor">Supervisor</SelectItem>
              <SelectItem value="Lead">Lead</SelectItem>
              <SelectItem value="Agent">Agent</SelectItem>
            </SelectContent>
          </Select>
          {/* Section 1 (iter-137) — discrete Role filter. */}
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="bg-zinc-950 border-zinc-700 text-white" data-testid="ops-staffing-role">
              <SelectValue placeholder="Role" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All roles</SelectItem>
              <SelectItem value="manager">Regional Manager</SelectItem>
              <SelectItem value="supervisor">Supervisor</SelectItem>
              <SelectItem value="crew_leader">Crew Leader</SelectItem>
              <SelectItem value="agent">Site Agent</SelectItem>
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="bg-zinc-950 border-zinc-700 text-white" data-testid="ops-staffing-status">
              <SelectValue placeholder="Dispatch" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="on-job">On-Job</SelectItem>
              <SelectItem value="assigned">Assigned</SelectItem>
              <SelectItem value="idle">Idle</SelectItem>
              <SelectItem value="blocked">Blocked</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      <Card className="bg-zinc-900 border-zinc-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="ops-staffing-table">
            <thead className="bg-zinc-950 text-zinc-400 uppercase text-[11px] tracking-widest">
              <tr>
                <th className="px-4 py-2 text-left">Name / Badge</th>
                <th className="px-4 py-2 text-left">Role</th>
                <th className="px-4 py-2 text-left">Position</th>
                <th className="px-4 py-2 text-left">Region</th>
                <th className="px-4 py-2 text-left">State</th>
                <th className="px-4 py-2 text-left">Schedule</th>
                <th className="px-4 py-2 text-left">Dispatch</th>
              </tr>
            </thead>
            <tbody>
              {roster.length === 0 && (
                <tr>
                  <td colSpan="7" className="px-4 py-10 text-center text-zinc-500"
                      data-testid="ops-staffing-empty">
                    No staffing agents match the current filters.
                  </td>
                </tr>
              )}
              {roster.map((e) => {
                const roleLabel = interpretRole(e, terms.view);
                const positionLabel = interpretPosition(e.role, e.position);
                const reg = e.region_code ? regionByCode[e.region_code] : null;
                const regionLabel = reg
                  ? `${e.region_code} ${(reg.name || '').replace(/^R\d+\s*/, '')}`.trim()
                  : (e.region_code || '—');
                const regionColor = reg?.color || '#6b7280';
                const state = e.state || inferState(e.region_code);
                const schedule = interpretSchedule(e);

                return (
                  <tr key={e.user_id || e.badge_number}
                      className="border-t border-zinc-800 hover:bg-zinc-950/60"
                      data-testid={`ops-staffing-row-${e.user_id || e.badge_number}`}>
                    <td className="px-4 py-2">
                      <div className="text-white font-semibold">{e.name || '—'}</div>
                      <div className="text-[11px] text-zinc-500 font-mono">{e.badge_number || ''}</div>
                    </td>
                    <td className="px-4 py-2">
                      <Badge className={
                        roleLabel === 'Valet/Cleaner' ? 'bg-[#C9A962] text-black' :
                        roleLabel === 'Valet' ? 'bg-sky-700 text-white' :
                        roleLabel === 'Cleaner' ? 'bg-emerald-700 text-white' :
                        'bg-zinc-700 text-white'
                      }>{roleLabel}</Badge>
                    </td>
                    <td className="px-4 py-2 text-zinc-200">{positionLabel}</td>
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-2">
                        <span
                          className="inline-block h-2.5 w-2.5 rounded-full flex-shrink-0"
                          style={{ backgroundColor: regionColor }}
                          data-testid={`ops-staffing-region-dot-${e.user_id || e.badge_number}`}
                        />
                        <span className="text-zinc-200 text-xs font-mono">{regionLabel}</span>
                      </div>
                    </td>
                    <td className="px-4 py-2 text-zinc-300">{state}</td>
                    <td className="px-4 py-2 text-zinc-200 text-xs">{schedule}</td>
                    <td className="px-4 py-2" data-testid={`ops-staffing-dispatch-${e.user_id || e.badge_number}`}>
                      {e.is_blocked ? (
                        <Badge className="bg-red-700 text-white">Blocked</Badge>
                      ) : (() => {
                        const ds = dispatchStatusOf(e);
                        if (ds === 'on-job')   return <Badge className="bg-amber-500 text-black">On-Job</Badge>;
                        if (ds === 'assigned') return <Badge className="bg-emerald-700 text-white">Assigned</Badge>;
                        return <Badge className="bg-zinc-700 text-white">Idle</Badge>;
                      })()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
